import React, { useEffect, useState } from "react";
import { cn } from "../../utils/cn";
import Icon, { type IconName } from "../AppIcon";

export interface ToastProps {
	id?: string;
	title?: string;
	message: string;
	type?: "success" | "error" | "warning" | "info";
	duration?: number;
	onClose?: () => void;
}

const Toast: React.FC<ToastProps> = ({
	title,
	message,
	type = "info",
	duration = 5000,
	onClose,
}) => {
	const [isVisible, setIsVisible] = useState(true);

	useEffect(() => {
		if (duration > 0) {
			const timer = setTimeout(() => {
				setIsVisible(false);
				setTimeout(() => onClose?.(), 300); // Wait for animation
			}, duration);

			return () => clearTimeout(timer);
		}
	}, [duration, onClose]);

	const handleClose = () => {
		setIsVisible(false);
		setTimeout(() => onClose?.(), 300);
	};

	const typeConfig = {
		success: {
			bg: "bg-success border-success",
			text: "text-white",
			icon: "CheckCircle2" as IconName,
		},
		error: {
			bg: "bg-error border-error",
			text: "text-white",
			icon: "AlertCircle" as IconName,
		},
		warning: {
			bg: "bg-warning border-warning",
			text: "text-white",
			icon: "AlertTriangle" as IconName,
		},
		info: {
			bg: "bg-primary border-primary",
			text: "text-white",
			icon: "Info" as IconName,
		},
	};

	const config = typeConfig[type];

	return (
		<div
			className={cn(
				"flex items-start gap-3 p-4 rounded-lg border shadow-lg min-w-[300px] max-w-[500px] transition-all duration-300",
				config.bg,
				isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2",
			)}
		>
			<Icon
				name={config.icon}
				size={20}
				className={cn("flex-shrink-0 mt-0.5", config.text)}
			/>
			<div className="flex-1 min-w-0">
				{title && (
					<h4 className={cn("font-semibold text-sm mb-1", config.text)}>
						{title}
					</h4>
				)}
				<p className={cn("text-sm", config.text)}>{message}</p>
			</div>
			<button
				onClick={handleClose}
				className={cn("flex-shrink-0 transition-colors", config.text, "hover:opacity-80")}
			>
				<Icon name="X" size={16} />
			</button>
		</div>
	);
};

export default Toast;
